<template>
    <dashboard-layout title="근무계약서 회신내역" fullHeight>
        <template slot="contents">
            <!-- 검색필터 -->
            <employment-contract-list-filter class="mb-12 mb-lg-20" style="flex: 0 0 auto" />

            <div class="mb-12 mb-lg-20" style="flex: 0 0 auto">
                <v-row align="center">
                    <v-col>
                        <v-row align="center" class="row--small">
                            <v-col cols="auto">
                                <texting-dialog v-bind="textingProps">
                                    <template #activator="{ attrs, on }">
                                        <v-btn v-bind="{ ...attrs, disabled: selected.length < 1 }" v-on="on" outlined color="primary" small>
                                            <span>선택 계약서 문자 발송</span>
                                        </v-btn>
                                    </template>
                                </texting-dialog>
                            </v-col>
                        </v-row>
                    </v-col>
                    <v-col cols="auto">
                        <v-select v-model="limit" :items="[10, 20]" hide-details outlined class="w-100px" />
                    </v-col>
                </v-row>
            </div>

            <template v-if="contracts.length">
                <v-data-table
                    v-model="selected"
                    v-bind="{
                        items,
                        headers,
                        mobileBreakPoint: $vuetify.breakpoint.thresholds.sm,
                    }"
                    show-select
                    disable-filtering
                    disable-pagination
                    disable-sort
                    hide-default-footer
                    class="v-sheet--outlined rounded-0"
                >
                    <template v-for="header in headers.filter((header) => header.hasOwnProperty('formatter'))" #[`item.${header.value}`]="{ value, item }">
                        <div :key="header.value">{{ header.formatter(value, item) }}</div>
                    </template>
                    <template #[`item.actions`]="{ item }">
                        <v-btn small block color="primary" :to="`${$route.path}/${item._id}`"> 상세 보기 </v-btn>
                    </template>
                </v-data-table>
                <v-pagination :value="page" :length="pageCount" :total-visible="11" color="primary" class="my-16" @input="(page) => $router.push({ query: { ...$route.query, page } })"></v-pagination>
            </template>

            <template v-else>
                <v-layout column justify-center align-center class="w-100 h-100 grey-f7 rounded-lg pa-20">
                    <p class="page-text--lg line-height-15 text-center mb-12 mb-lg-32">
                        안녕하세요, 담당자님. <br />
                        근무자가 없습니다.
                    </p>
                </v-layout>
            </template>

            <v-fade-transition>
                <v-overlay v-show="loading" color="white">
                    <v-progress-circular size="100" width="5" color="primary" indeterminate />
                </v-overlay>
            </v-fade-transition>
        </template>
    </dashboard-layout>
</template>

<script>
import api from "@/api";
import DashboardLayout from "@/components/dashboard/dashboard-layout.vue";
import EmploymentContractListFilter from "@/components/dashboard/company/employment/contract/employment-contract-list-filter.vue";
import TextingDialog from "@/components/dashboard/company/texting/texting-dialog.vue";

const headers = [
    {
        text: "#",
        value: "index",
        width: 80,
        align: "end",
    },
    {
        text: "근로자명",
        value: "application.resume.personalInformation.name",
        width: 160,
    },
    {
        text: "계약명 (계약서 명칭)",
        value: "subject",
        formatter: (value, item) => `${value} (${item?.form?.name})`,
    },
    {
        text: "상태",
        value: "isSigned",
        formatter: (value) => (value ? "서명 완료" : "서명 대기"),
        width: +96,
    },
    {
        text: "생성일",
        value: "createdAt",
        formatter: (value) => value?.toDateTime?.() || value || "-",
        width: 128,
    },
    {
        text: "서명일",
        value: "signedAt",
        formatter: (value) => value?.toDateTime?.() || value || "-",
        width: 128,
    },
    {
        text: "",
        value: "actions",
        width: 140,
    },
].map((item, index, array) => ({ ...item, divider: index < array.length - 1 }));

export default {
    components: {
        DashboardLayout,
        EmploymentContractListFilter,
        TextingDialog,
    },
    data: () => ({
        contracts: [],
        summary: { totalCount: 0 },

        limit: 10,
        loading: false,

        selected: [],
        headers,
    }),
    computed: {
        isAllSelected() {
            return !this.contracts.some((contract) => !this.selected.some((item) => item._id == contract._id));
        },
        items() {
            return this.contracts.map((item, index) => ({ ...item, index: this.summary.totalCount - (this.page - 1) * this.limit - index }));
        },
        page() {
            return +(this.$route.query.page || "1");
        },
        skip() {
            return (this.page - 1) * this.limit;
        },
        pageCount() {
            return Math.ceil(this.summary.totalCount / this.limit) || 1;
        },
        params() {
            let { ...query } = this.$route.query;

            delete query.page;

            return { ...query };
        },
        textingProps() {
            let value = this.selected.reduce((items, contract) => {
                let { application } = contract;

                if (!items.some((item) => item._id == application._id)) {
                    items.push(application);
                }

                return items;
            }, []);

            return { value, applications: value };
        },
    },
    mounted() {
        this.init().then(this.search);
    },
    watch: {
        params() {
            this.init().then(this.search);
        },
        limit() {
            this.init().then(this.search);
        },
    },
    methods: {
        async init() {
            this.contracts = [];
            this.summary = { totalCount: 0 };
            this.selected = [];
        },
        async search() {
            if (this.loading) return;
            else this.loading = true;
            try {
                let { skip, limit, params } = this;
                const { summary, contracts } = await api.v1.company.employment.contracts.gets({
                    headers: { skip, limit },
                    params,
                });
                this.summary = summary;
                this.contracts = contracts;
            } finally {
                this.loading = false;
            }
        },

        async remove(item) {
            if (!confirm("삭제하시겠습니까?")) return;

            if (this.loading) return;
            else this.loading = true;

            try {
                const { contract } = await api.v1.company.employment.contracts.delete({ _id: item._id });

                if (contract?.isSigned && contract?._signedForm) {
                    await api.v1.company.employment.forms.signed.delete({
                        _id: contract._signedForm,
                    });
                }
                alert("삭제되었습니다.");
            } finally {
                this.loading = false;
                this.search();
            }
        },

        toggleSelect(item) {
            let index = this.selected.findIndex(({ _id }) => _id == item._id);
            if (-1 < index) this.selected.splice(index, 1);
            else this.selected.push(item);
        },

        toggleSelectAll() {
            if (this.isAllSelected) this.selected = [];
            else this.selected = [...this.contracts];
        },
    },
};
</script>

<style lang="scss" scoped>
::v-deep {
    th,
    td {
        &:first-child {
            border-right: 1px solid var(--border-color);

            .v-input--selection-controls__input {
                margin: 0;

                i.v-icon {
                    &.mdi-checkbox-marked,
                    &.mdi-minus-box {
                        color: var(--v-primary-base) !important;
                    }
                }
            }
        }
    }
}
</style>
